<template>
  <v-dialog v-model="internalValue" width="800px" persistent>
    <template v-slot:activator="{ on, attrs }">
      <div v-bind="attrs" v-on="on">
        <v-card>
          <v-card-text class="card-box">
            <v-row>
              <v-col class="pa-0 pr-3 d-inline-flex justify-space-between" style="cursor: pointer">
                <span>
                  <b>{{ field.label }}</b>
                  <v-chip small class="ml-2">{{ getFieldTypeLabel(field.type) }}</v-chip>
                  <v-chip v-if="field.extra_data" small class="ml-2">Campo extra</v-chip>
                </span>
                <div @click.stop>
                  <mf-button v-if="!isFirstField" color="cinza-0" type="icon" icon="keyboard_arrow_up" size="xs" @click="$emit('move-up', field)" />

                  <mf-button v-if="!isLastField" color="cinza-0" type="icon" icon="keyboard_arrow_down" size="xs" @click="$emit('move-down', field)" />

                  <mf-button v-if="!field.mandatory" color="cinza-0" type="icon" icon="close" size="xs" @click="$emit('remove', field)" />
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>
    </template>

    <v-card class="hotsite-edit-field">
      <v-card-title>Editando {{ lazyField.label }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6" class="py-1">
            <label class="detail-title">Campo</label>
            <v-text-field v-model="field.name" disabled outlined autofocus hide-details />
          </v-col>

          <v-col cols="6" class="py-1">
            <label class="detail-title">Label</label>
            <v-text-field v-model="lazyField.label" :disabled="lazyField.mandatory" outlined autofocus hide-details />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6" class="py-1">
            <label class="detail-title">Tipo</label>
            <v-select v-model="lazyField.type" disabled :items="fieldTypes" item-text="text" item-value="value" outlined hide-details />
          </v-col>

          <v-col v-if="editable" cols="6" class="py-1">
            <label class="detail-title">Largura</label>
            <v-select
              v-model="lazyField.width"
              :disabled="lazyField.mandatory"
              :items="fieldWidths"
              item-text="text"
              item-value="value"
              outlined
              hide-details
            />
          </v-col>
        </v-row>

        <div v-if="editable">
          <div v-if="maskable">
            <v-row>
              <v-col cols="6" class="py-1">
                <label class="detail-title">Máscara</label>
                <v-combobox v-model="lazyField.mask" dense :disabled="lazyField.mandatory" multiple append-icon chips deletable-chips outlined hide-details />
              </v-col>

              <v-col cols="6" class="py-1">
                <label class="detail-title">Placeholder</label>
                <v-text-field v-model="lazyField.placeholder" :disabled="lazyField.mandatory" outlined autofocus hide-details />
              </v-col>
            </v-row>
          </div>

          <div v-if="optionable">
            <v-row>
              <v-col cols="3" class="py-0 pt-1">
                <label class="detail-title">Opções</label>
              </v-col>
            </v-row>

            <v-row>
              <v-col v-for="(option, index) of lazyField.options" :key="`option-${index}`" class="py-0">
                <span>{{ option.label }} - [{{ option.value }}]</span>
              </v-col>
            </v-row>
          </div>
          <v-row>
            <v-col cols="3" class="py-0 pt-1">
              <label class="detail-title">Validações</label>
            </v-col>
          </v-row>

          <v-row align="center">
            <v-col cols="3" class="py-0">
              <v-checkbox v-model="lazyField.validators.required" class="mt-0" label="Obrigatório" color="primary" hide-details single-line />
            </v-col>

            <v-col v-if="lazyField.type === 'FormText'" cols="3" class="py-0">
              <v-checkbox
                v-model="lazyField.validators.alpha_spaces"
                :disabled="lazyField.mandatory"
                class="mt-0"
                label="Alpha Spaces"
                color="primary"
                hide-details
                single-line
              />
            </v-col>

            <v-col v-if="lazyField.type === 'FormText'" cols="3" class="py-0 d-inline-flex justify-space-between align-center">
              <v-checkbox
                v-model="validatorsMinEnabled"
                :disabled="lazyField.mandatory"
                class="mt-0 pr-3"
                label="Mínimo"
                color="primary"
                hide-details
                single-line
              />
              <v-text-field
                v-model.number="lazyField.validators.min"
                :disabled="lazyField.mandatory || !validatorsMinEnabled"
                class="pa-0 ma-0"
                autofocus
                hide-details
              />
            </v-col>

            <v-col v-if="lazyField.type === 'FormText'" cols="3" class="py-0 d-inline-flex justify-space-between align-center">
              <v-checkbox
                v-model="validatorsMaxEnabled"
                :disabled="lazyField.mandatory"
                class="mt-0 pr-3"
                label="Máximo"
                color="primary"
                hide-details
                single-line
              />
              <v-text-field
                v-model.number="lazyField.validators.max"
                :disabled="lazyField.mandatory || !validatorsMaxEnabled"
                class="pa-0 ma-0"
                autofocus
                hide-details
              />
            </v-col>
          </v-row>

          <div v-if="fieldCustomValidators && fieldCustomValidators.length">
            <v-row>
              <v-col cols="3" class="py-0 pt-1">
                <label class="detail-title">Validações customizadas</label>
              </v-col>
            </v-row>

            <v-row>
              <v-col v-for="([validator, param], index) in fieldCustomValidators" :key="`custom-validator-${index}`"> {{ validator }}({{ param }})</v-col>
            </v-row>
          </div>
        </div>
        <mf-action-buttons
          class="mt-2"
          :primary-button="{
            text: 'Salvar',
            action: save,
            isVisible: true,
          }"
          :extra-button="{
            text: 'Cancelar',
            action: cancel,
            isVisible: true,
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { customValidators } from '@/modules/configuracoes-admin/assets/fields'

export default {
  props: {
    field: {
      type: Object,
      required: true,
    },
    isFirstField: {
      type: Boolean,
      default: false,
    },
    isLastField: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    lazyValue: false,
    lazyField: {
      validators: {},
    },
    validatorsMinEnabled: false,
    validatorsMaxEnabled: false,
  }),
  computed: {
    fieldCustomValidators() {
      return Object.entries(this.lazyField?.validators || {}).filter(([validator]) => customValidators.includes(validator))
    },
    editable() {
      return !['FormAddress'].includes(this.lazyField.type)
    },
    maskable() {
      return ['FormText'].includes(this.lazyField.type)
    },
    optionable() {
      return ['FormSelect', 'FormCheckbox', 'FormRadio'].includes(this.lazyField.type)
    },
    fieldWidths() {
      return [
        { text: 'Inteiro', value: 100 },
        { text: 'Metade', value: 50 },
      ]
    },
    fieldTypes() {
      return [
        { text: 'Texto', value: 'FormText' },
        { text: 'Checkbox', value: 'FormCheckbox' },
        { text: 'Radio', value: 'FormRadio' },
        { text: 'Select', value: 'FormSelect' },
        { text: 'Endereço', value: 'FormAddress' },
        { text: 'Senha', value: 'FormPassword' },
      ]
    },
    internalValue: {
      get() {
        return this.lazyValue
      },
      set(value) {
        this.lazyValue = value
      },
    },
  },
  watch: {
    field: {
      handler(value) {
        this.setupField(value)
      },
      immediate: true,
    },
  },
  mounted() {
    this.setupField(this.field)
  },
  methods: {
    getFieldTypeLabel(fieldType) {
      return this.fieldTypes.find((type) => type.value === fieldType)?.text
    },
    setupField(value) {
      this.lazyField = cloneDeep(value)
      this.lazyField.validators = this.lazyField?.validators || {}
      this.validatorsMinEnabled = Boolean(this.lazyField?.validators?.min)
      this.validatorsMaxEnabled = Boolean(this.lazyField?.validators?.max)
    },
    save() {
      this.$emit('update:field', this.lazyField)
      this.internalValue = false
    },
    cancel() {
      this.internalValue = false
    },
  },
}
</script>

<style lang="scss">
.hotsite-edit-field {
  .detail-title {
    opacity: 0.8;
    font-size: 15px;
    font-weight: bold;
    line-height: 1.2;
    letter-spacing: 0.24px;
    color: $preto;
    margin-bottom: 8px;
  }
}

.card-box {
  padding: 3px 5px 5px 30px;
  p {
    margin: 0;
    width: 220px;
    text-align: left;
    font-size: 12px;
    margin: 0 !important;
  }
}
</style>
